import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Column,
  CustomAction,
  DataTableComponent,
  EmptyTableComponent,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import {
  DateTimePipe,
  NimmstaTimeAgoPipe,
  StatusBadgePipe,
  TimePipe,
} from '@nimmsta/ng-commons-lib/pipes';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { JobStatus } from '@nimmsta/ng-commons-lib/models';
import {
  ReusableTabsComponent,
  TabsConfiguration,
} from '@nimmsta/ng-commons-lib/reusable-tabs';
import { WorkflowService } from '@nimmsta/ng-commons-lib/services/workflow';
import { AppDialogService } from '@nimmsta/ng-commons-lib/services/app-dialog';
import { ActivatedRoute } from '@angular/router';
import { WorkflowEvent } from '../models/workflowEvent';
import { WorkflowEventService } from '../services/workflow-event.service';
import { WorkflowExecution } from '../models/workflowExecution';
import { WorkflowExecutionService } from '../services/workflow-execution.service';
import { DownloadDataAction } from './downloadDataAction';
import { DownloadResultAction } from './downloadResultAction';
import { ShowDataAction } from './showDataAction';
import { CodeComponent } from '../dataconnection/types/http/code.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EditorComponent } from 'ngx-monaco-editor-v2';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

_('workflow.execution.title');

@Component({
  selector: 'app-workflow-execution-detail',
  templateUrl: './workflow-execution-detail.component.html',
  imports: [
    TranslatePipe,
    EditorComponent,
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    HeaderBarComponent,
    DataTableComponent,
    EmptyTableComponent,
    ErrorComponent,
    NimmstaTimeAgoPipe,
    ReusableTabsComponent,
    StatusBadgePipe,
    DateTimePipe,
  ],
})
export class WorkflowExecutionDetailComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private service = inject(WorkflowEventService);
  private executionService = inject(WorkflowExecutionService);
  private timePipe = inject(TimePipe);
  private route = inject(ActivatedRoute);
  private modalService = inject(AppDialogService);
  private translate = inject(TranslateService);
  private workflowService = inject(WorkflowService);
  private http = inject(HttpClient);

  source!: NimmstaDataSource<WorkflowEvent>;

  @ViewChild(DataTableComponent)
  private tableComponent?: DataTableComponent<WorkflowEvent>;

  @ViewChild('stepTemplate') private stepTemplate!: TemplateRef<unknown>;
  @ViewChild('dataTemplate') private dataTemplate!: TemplateRef<unknown>;

  error?: HttpErrorResponse;

  workflowId?: string;

  workflowExecutionId?: string;

  execution?: WorkflowExecution;

  executionLoading = false;

  customActions: CustomAction<WorkflowEvent>[] = [];

  private reloadTimeout?: ReturnType<typeof setTimeout>;

  editorOptions = {
    readOnly: true,
    automaticLayout: true,
    theme: 'vs-dark',
    language: 'json',
    fontSize: '14',
    wordWrap: 'on',
    scrollbar: {
      alwaysConsumeMouseWheel: false,
    },
  };

  currentStep?: string;

  currentResult?: string;

  currentData?: string;

  editorTabsConfig!: TabsConfiguration;

  title = this.translate.instant('workflow.event.title');

  get columns(): Column[] {
    return [
      new Column('name', 'workflow.event.name'),
      new Column('condition', 'workflow.event.condition'),
      new Column('eventDate', 'time', { pipe: this.timePipe }),
    ];
  }

  ngAfterViewInit(): void {
    this.editorTabsConfig = {
      tabs: [
        {
          id: 'step',
          labelKey: 'workflow.execution.currentStep',
          template: this.stepTemplate,
        },
        {
          id: 'data',
          labelKey: 'workflow.execution.data',
          template: this.dataTemplate,
        },
      ],
      defaultTab: 'step',
      storageKey: 'workflow-execution-detail-editor-tab',
      enableRouting: false,
      disableStorage: true,
    };
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.workflowId = params.get('workflow_id')!;
      this.workflowExecutionId = params.get('id')!;
      this.service.currentWorkflowExecutionId = this.workflowExecutionId;
      this.source = new NimmstaDataSource<WorkflowEvent>(this.service);
      this.loadExecution();
    });
    this.workflowService.get(this.workflowId!).subscribe((workflow) => {
      this.title += ' - ' + workflow.name;
    });
    const showDataAction = new ShowDataAction('workflow.event.show-data');
    this.customActions.push(showDataAction);
    showDataAction.subscribe((id) => this.showData(id));
    const dataAction = new DownloadDataAction('workflow.event.show-data');
    this.customActions.push(dataAction);
    dataAction.subscribe((id) => this.loadData(id));
    const resultAction = new DownloadResultAction('workflow.event.show-result');
    this.customActions.push(resultAction);
    resultAction.subscribe((id) => this.loadResult(id));
  }

  ngOnDestroy(): void {
    this.clearReloadTimeout();
  }

  loadExecution(): void {
    this.clearReloadTimeout();
    this.executionLoading = true;
    this.executionService.get(this.workflowExecutionId!).subscribe({
      next: (execution: WorkflowExecution) => {
        this.execution = execution;
        this.currentResult = JSON.stringify(execution.result, null, 2);
        this.currentData = JSON.stringify(execution.data, null, 2);
        const currentStep =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (execution?.data as any)?.workflow?.currentWorkflowStep ??
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (execution?.data as any)?.data?.workflow?.currentWorkflowStep ??
          {};
        delete currentStep.previousStep;
        this.currentStep = JSON.stringify(currentStep, null, 2);
        this.executionLoading = false;
        if (execution.status === JobStatus.IN_PROGRESS) {
          this.reloadTimeout = setTimeout(() => {
            this.loadExecution();
            this.reloadEvents();
          }, 10000);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
        this.executionLoading = false;
      },
    });
  }

  private clearReloadTimeout(): void {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout);
      this.reloadTimeout = undefined;
    }
  }

  private reloadEvents(): void {
    this.tableComponent?.loadDataPage();
  }

  showData(id: string): void {
    const eventData = this.getSelectedObject(id)?.data;
    const data = {
      code: eventData as string,
      title: 'workflow.event.data',
      undefined,
      codeLanguage: 'json',
    };
    this.modalService.open(CodeComponent, {
      data,
      size: 'xl',
    });
  }

  loadData(id: string): void {
    const dataUrl = this.getSelectedObject(id)
      ?.urls?.filter((url) => url.indexOf('data.json') > 0)
      .pop();
    if (!dataUrl) return;
    this.http.get(dataUrl, { responseType: 'text' }).subscribe((result) => {
      const content = JSON.stringify(JSON.parse(result), null, 2);
      const data = {
        code: content,
        title: 'workflow.event.data',
        undefined,
        codeLanguage: 'json',
      };
      this.modalService.open(CodeComponent, { data, size: 'xl' });
    });
  }

  loadResult(id: string): void {
    const resultUrl = this.getSelectedObject(id)
      ?.urls?.filter((url) => url.indexOf('result.json') > 0)
      .pop();
    if (!resultUrl) return;
    this.http.get(resultUrl, { responseType: 'text' }).subscribe((result) => {
      const content = JSON.stringify(JSON.parse(result), null, 2);
      const data = {
        code: content,
        title: 'workflow.event.result',
        undefined,
        codeLanguage: 'json',
      };
      this.modalService.open(CodeComponent, { data, size: 'xl' });
    });
  }

  getSelectedObject(id: string): WorkflowEvent | undefined {
    return this.source.data?.content.filter((d) => id === d.id).pop();
  }
}
